import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { HomePageLayout as HomePage } from 'src/domains/layouts/layouts/homepage/Homepage';
import { Footer } from 'src/domains/layouts/webview/modules/layoutSkeleton/Footer';
import { PageNotFoundFallBackLoader } from 'src/domains/layouts/layouts/utilityPages/errorPage/PageNotFound';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { ssrReactLazy } from 'src_common/common/mobx-utils/ResourceSSR';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { AccountSidebar } from 'src/domains/players/webview/components/Account/accountParts/AccountSidebar';
import { FeatureState } from 'src/domains/layouts/config/features/featureState/FeatureState';

import { EventLayout as EventPage } from 'src/domains/sportsbook/webview/layouts/eventPage/EventPage';
import { CompetitionPage } from 'src/domains/sportsbook/webview/layouts/competitionPage/CompetitionPage';
import { InPlayLayout as InPlayPage } from 'src/domains/sportsbook/webview/layouts/inPlayPage/InPlayPage';
import { CasinoPage } from 'src/domains/casino/webview/layouts/casinoPage/CasinoPage';
import { SearchPage as Search } from 'src/domains/layouts/layouts/seachPage/SearchPage';
import { NavigationSports } from 'src/domains/layouts/layouts/navigationSportsPage/NavigationSportsPage';
import { Notifications } from 'src/domains/layouts/webview/components/notificationsPage/NotificationsPage';
import { LiveCasinoPage } from 'src/domains/casino/webview/layouts/liveCasinoPage/LiveCasinoPage';
import { SpecialSportPage } from 'src/domains/layouts/layouts/specialSportPage/SpecialSportPage';
import { SportPage } from 'src/domains/layouts/layouts/sportPage/SportPage';
import { RaceCardPage } from 'src/domains/sportsbook/webview/layouts/raceCardPage/RaceCardPage';
import { PromotedEvents } from 'src/domains/layouts/layouts/promotedEvents/PromotedEvents';
import { VirtualsPage } from 'src/domains/casino/webview/layouts/virtualsPage/VirtualsPage';

import { ClosureModeComponent } from 'src/domains/players/webview/components/Account/closureModeComponent/ClosureModeComponent';
import { MidnightMigrationScreen } from 'src/domains/players/webview/components/Account/midnightMigrationScreen/MidnightMigrationScreen';

const PageNotFound = ssrReactLazy(
    async () => (await import('src/domains/layouts/layouts/utilityPages/errorPage/PageNotFound')).PageNotFound
);

interface LayoutType {
    content: JSX.Element;
    footer: JSX.Element | null;
    aside: JSX.Element | null;
}

const renderLayoutWithSidebar = (content: JSX.Element): LayoutType => {
    const common = useCommon();
    const router = StarRouter.get(common);
    const {
        componentVariants: { sportsbookVariant },
    } = ConfigComponents.get(common).config;

    const accountView = router.accountView;
    const isSignup = accountView?.account === 'signup';

    const renderFooter =
        sportsbookVariant !== DefaultComponentVariantType.SECONDARY ||
        router.currentView?.name === 'casino' ||
        router.currentView?.name === 'virtuals' ||
        router.currentView?.name === 'live-casino';

    const isNotificationsList =
        router.currentView?.name === 'notifications-list' || accountView?.account === 'terms-and-conditions-promos';

    return {
        content,
        footer: renderFooter ? <Footer currentView={router.currentView} /> : null,
        aside: (
            <AccountSidebar
                isSignUp={isSignup}
                currentView={router.currentView}
                promotionSlug={router.freeParams.promo ?? 'homepage'}
                isNotificationsList={isNotificationsList}
            />
        ),
    };
};

export const renderLayout = (): LayoutType | null => {
    const common = useCommon();
    const router = StarRouter.get(common);
    const { hiddenSportsList, eventsOnLaterHomePage } = ConfigComponents.get(common).config;
    const { allowCasinoInIOSWrapperNew } = FeatureState.get(common);

    const { currentView, accountView } = router;

    const isNotificationsList =
        router.currentView?.name === 'notifications-list' || accountView?.account === 'terms-and-conditions-promos';

    if (currentView !== null) {
        if (currentView.name === 'homepage') {
            return renderLayoutWithSidebar(
                <HomePage
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={true}
                />
            );
        }

        if (currentView.name === 'sports') {
            return renderLayoutWithSidebar(
                <HomePage
                    accountView={accountView}
                    currentView={{ name: 'homepage' }}
                    isSearchActive={true}
                />
            );
        }

        if (currentView.name === 'competition') {
            return renderLayoutWithSidebar(
                <CompetitionPage
                    currentView={currentView}
                    accountView={accountView}
                    isSearchActive={true}
                />
            );
        }

        if (currentView.name === 'sport') {
            if (hiddenSportsList.includes(currentView.id)) {
                return null;
            }

            return renderLayoutWithSidebar(
                <SportPage
                    currentView={currentView}
                    accountView={accountView}
                    isSearchActive={true}
                    isAmericanFootball={currentView.id === 'americanfootball'}
                />
            );
        }

        if (currentView.name === 'event') {
            return renderLayoutWithSidebar(
                <EventPage
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={true}
                    isNotificationsList={isNotificationsList}
                />
            );
        }

        if (currentView.name === 'racecard') {
            const sport = currentView.sport;

            if (sport === 'horseracing' || sport === 'greyhoundracing') {
                const racecardBuildIds = currentView.collection === 'race-meetings' ? currentView.racecardBuildIds : [];

                return renderLayoutWithSidebar(
                    <RaceCardPage
                        sport={sport}
                        currentView={currentView}
                        accountView={accountView}
                        isSearchActive={true}
                        racecardBuildIds={racecardBuildIds}
                        racecardCollection={currentView.collection}
                        racecardSelected={currentView.selected}
                    />
                );
            } else {
                return null;
            }
        }

        if (currentView.name === 'virtuals' && allowCasinoInIOSWrapperNew === true) {
            return renderLayoutWithSidebar(
                <VirtualsPage
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={false}
                />
            );
        }

        if (currentView.name === 'sport-special') {
            return renderLayoutWithSidebar(
                <SpecialSportPage
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={true}
                />
            );
        }

        if (currentView.name === 'inplay') {
            return renderLayoutWithSidebar(
                <InPlayPage
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={true}
                />
            );
        }

        if (currentView.name === 'casino') {
            if (allowCasinoInIOSWrapperNew === false) {
                return null;
            }

            return renderLayoutWithSidebar(
                <CasinoPage
                    currentView={currentView}
                    accountView={accountView}
                    isSearchActive={false}
                />
            );
        }

        if (currentView.name === 'live-casino') {
            if (allowCasinoInIOSWrapperNew === false) {
                return null;
            }

            return renderLayoutWithSidebar(
                <LiveCasinoPage
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={false}
                />
            );
        }

        if (currentView.name === 'search') {
            return renderLayoutWithSidebar(
                <Search
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={true}
                />
            );
        }

        if (currentView.name === 'navigation-sports') {
            return renderLayoutWithSidebar(
                <NavigationSports
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={true}
                />
            );
        }

        if (currentView.name === 'notifications-list') {
            return renderLayoutWithSidebar(
                <Notifications
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={true}
                    isNotificationsList={isNotificationsList}
                />
            );
        }

        if (currentView.name === 'promoted-events' && eventsOnLaterHomePage) {
            return renderLayoutWithSidebar(
                <PromotedEvents
                    accountView={accountView}
                    currentView={currentView}
                    isSearchActive={true}
                />
            );
        }
    }

    return null;
};

export const MainLayout = observer('MainLayout', () => {
    const common = useCommon();
    const router = StarRouter.get(common);
    const { currentView, accountView } = router;
    const layout = renderLayout();

    const { closureModeWithLogin, closureMode, midnightMigrationScreen } = ConfigComponents.get(common).config;

    return (
        <>
            {layout === null ? (
                <React.Suspense fallback={<PageNotFoundFallBackLoader />}>
                    <PageNotFound
                        accountView={accountView}
                        currentView={currentView}
                    />
                </React.Suspense>
            ) : (
                layout.content
            )}
            {midnightMigrationScreen ? <MidnightMigrationScreen /> : null}
            {closureModeWithLogin ? <ClosureModeComponent /> : null}
            {closureMode ? <ClosureModeComponent alreadyClosed /> : null}
            {layout === null ? null : layout.footer}
            {layout === null ? null : layout.aside}
        </>
    );
});
